/**
 * Auth Context for Web Dashboard
 * Handles authentication and organization context
 */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, type UserOrganization } from '../lib/api';

interface User {
  id: string;
  username: string;
  role: 'admin' | 'user';
  organizations: UserOrganization[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isGlobalAdmin: boolean;
  isOrgAdmin: boolean;
  organizations: UserOrganization[];
  currentOrganizationId: string | null;
  currentOrganization: UserOrganization | null;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  setCurrentOrganizationId: (orgId: string | null) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'ndexpense_admin_token';
const USER_KEY = 'ndexpense_admin_user';
const ORG_KEY = 'ndexpense_admin_org';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentOrganizationId, setCurrentOrganizationIdState] = useState<string | null>(null);

  useEffect(() => {
    // Load stored auth on mount
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);
    const storedOrg = localStorage.getItem(ORG_KEY);

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        api.setToken(storedToken);

        // Restore organization context
        if (storedOrg) {
          setCurrentOrganizationIdState(storedOrg);
          api.setOrganizationId(storedOrg);
        } else if (parsedUser.organizations?.length === 1) {
          // Auto-select single org
          const orgId = parsedUser.organizations[0].id;
          setCurrentOrganizationIdState(orgId);
          api.setOrganizationId(orgId);
          localStorage.setItem(ORG_KEY, orgId);
        }
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(ORG_KEY);
      }
    }

    setIsLoading(false);
  }, []);

  const setCurrentOrganizationId = (orgId: string | null) => {
    const previousOrgId = currentOrganizationId;
    setCurrentOrganizationIdState(orgId);
    api.setOrganizationId(orgId);
    if (orgId) {
      localStorage.setItem(ORG_KEY, orgId);
    } else {
      localStorage.removeItem(ORG_KEY);
    }

    // Dispatch event for pages to refresh data (only if org actually changed)
    if (orgId !== previousOrgId) {
      window.dispatchEvent(new CustomEvent('organizationChanged', { detail: { id: orgId } }));
    }
  };

  const login = async (
    username: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    const result = await api.login(username, password);

    if (result.success && result.data) {
      const { token: newToken, user: newUser } = result.data;

      // Allow global admins OR organization owners/admins
      const isGlobalAdmin = newUser.role === 'admin';
      const isOrgAdmin = newUser.organizations?.some(
        (org: UserOrganization) => org.role === 'owner' || org.role === 'admin'
      );

      if (!isGlobalAdmin && !isOrgAdmin) {
        return { success: false, error: 'Admin access required' };
      }

      setToken(newToken);
      setUser(newUser);
      api.setToken(newToken);

      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(USER_KEY, JSON.stringify(newUser));

      // Auto-select first organization or single org
      if (newUser.organizations?.length > 0) {
        const orgId = newUser.organizations[0].id;
        setCurrentOrganizationId(orgId);
      }

      return { success: true };
    }

    return {
      success: false,
      error: result.error?.message || 'Login failed',
    };
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setCurrentOrganizationIdState(null);
    api.setToken(null);
    api.setOrganizationId(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ORG_KEY);
  };

  const organizations = user?.organizations || [];
  const currentOrganization = organizations.find(o => o.id === currentOrganizationId) || null;
  const isGlobalAdmin = user?.role === 'admin';
  const isOrgAdmin = !isGlobalAdmin && organizations.some(
    org => org.role === 'owner' || org.role === 'admin'
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isAuthenticated: !!token && !!user,
        isGlobalAdmin: !!isGlobalAdmin,
        isOrgAdmin: !!isOrgAdmin,
        organizations,
        currentOrganizationId,
        currentOrganization,
        login,
        logout,
        setCurrentOrganizationId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
